import { FC, useEffect, useMemo, useState } from "react";
import { Checkbox, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { useTranslate } from "@/hooks";
import { TCustomMultiSelect } from "./CustomMultiSelect.props";
import Hint from "../../HOC/Hint/Hint";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

const CustomMultiSelect: FC<TCustomMultiSelect> = ({
	variantsList,
	values,
	setState,
	elementWidth,
	hint = "",
	height = 40,
	width = 120,
	mt = "0",
	children,
	...props
}) => {
	const [currentValues, setCurrentValues] = useState<string[]>(values ?? []);

	const [selectWidth, setSelectWidth] = useState<number>(200);
	const translate = useTranslate();

	useEffect(() => {
		setCurrentValues(values);
	}, [values]);

	useEffect(() => {
		const longestElement = variantsList.length > 0 ? variantsList.reduce((a, b) => ((a.title ?? "").length > (b.title ?? "").length ? a : b)) : undefined;

		const maxWidth = longestElement
			? translate(longestElement.title).length * 10 > 100
				? translate(longestElement.title).length * 10
				: translate(longestElement.title).length * 10
			: 200;
		setSelectWidth(maxWidth);
	}, [variantsList]);

	const valueToTitleList = useMemo(() => variantsList.reduce<Record<string, string>>((acc, el) => ({ ...acc, [el.value]: el.title }), {}), [variantsList]);

	const handleChange = (event: SelectChangeEvent<typeof currentValues>) => {
		const {
			target: { value },
		} = event;

		// Для множественного выбора value всегда будет массивом
		const newValues = typeof value === "string" ? value.split(",") : value;

		setCurrentValues(newValues);
		if (setState) {
			setState(newValues); // Теперь передаем массив строк
		}
	};

	return (
		<Hint hint={hint}>
			<FormControl
				sx={{
					mt,
					width: elementWidth === "full" ? "100%" : elementWidth === "fixed" ? selectWidth : width,
				}}
			>
				<InputLabel id="demo-multiple-chip-label" sx={{ "&.MuiInputLabel-root:not(.Mui-focused):not(.MuiFormLabel-filled)": { transform: "translate(10px, 7px) " } }}>
					{props.label}
				</InputLabel>
				<Select
					labelId="select-label"
					id="select-helper"
					value={currentValues}
					multiple
					label={props.label ?? ""}
					onChange={handleChange}
					sx={{
						width: elementWidth === "full" ? "100%" : elementWidth === "fixed" ? selectWidth : width,
						height: "auto",
						minHeight: height,
						"& .MuiSelect-select": {
							padding: "0 8px !important",
							display: "flex",
							alignItems: "center",
							lineHeight: 0.7,
							whiteSpace: "normal",
							wordBreak: "break-word",
							flexWrap: "wrap",
							height: "auto !important",
							minHeight: `${height}px !important`,
						},
						"& .MuiOutlinedInput-notchedOutline legend": {
							padding: 0,
							fontSize: "10px",
							lineHeight: "1.2",
						},
						"& .MuiOutlinedInput-notchedOutline legend > span": {
							display: "inline-block",
							fontSize: "10px",
							lineHeight: "1.2",
						},
					}}
					renderValue={(selected) => (
						<div
							style={{
								display: "flex",
								flexWrap: "wrap",
								gap: "4px",
								padding: "8px 0",
							}}
						>
							{Array.isArray(selected)
								? selected.map((value) => (
										<div
											key={value}
											style={{
												padding: "4px 8px",
												backgroundColor: "rgba(0, 0, 0, 0.04)",
												borderRadius: "8px",
											}}
										>
											{valueToTitleList[value]}
										</div>
								  ))
								: selected}
						</div>
					)}
					MenuProps={MenuProps}
					{...props}
				>
					{variantsList.map((variant: { value: string; title: string }) => (
						<MenuItem
							key={variant.value}
							value={variant.value}
							sx={{
								padding: 0,
								minHeight: "auto",
							}}
						>
							<Checkbox size="small" checked={currentValues.indexOf(variant.value) > -1} />
							{variant.title}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Hint>
	);
};

export default CustomMultiSelect;
